<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Users",
};
</script>

<script setup lang="ts">
import UsersTable from "@/components/models/user/UsersTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import DealerService from "@/api/dealer";
import FamilyService from "@/api/family";
import RoleService from "@/api/role";
import UserService, { type TUser } from "@/api/user";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { generateFormOptions, type PaginatedType, type VFormFieldChoice, type VFormOptions } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Users",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.USER);
    },
  },
});

const snackStore = useSnackStore();
const userFetchEnabled = ref(false);

const userOptions = ref<VFormOptions>({});
const familiesChoices = ref<VFormFieldChoice<any>[]>([]);
const dealersChoices = ref<VFormFieldChoice<any>[]>([]);
const rolesChoices = ref<VFormFieldChoice<any>[]>([]);

const { can } = useAbility<AppAbility>();

const queriesTableParams: Record<string, ServerTableParamsType> = {
  users: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: users,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["users"],
  queryFn: async (): Promise<PaginatedType<TUser[]> | undefined> => {
    try {
      const [result, options, rolesSelectable, familiesSelectable, dealersSelectable] = await Promise.all([
        UserService.user.getAll(queriesTableParams.users),
        UserService.user.getOptions(),
        can(ActionsEnum.VIEW, SubjectsEnum.ROLE) ? RoleService.role.getAllSelectable() : [],
        can(ActionsEnum.USE, SubjectsEnum.FAMILY) ? FamilyService.family.getAllSelectable() : [],
        can(ActionsEnum.USE, SubjectsEnum.DEALER) ? DealerService.dealer.getAllSelectable() : [],
      ]);

      userOptions.value = generateFormOptions(options.data.actions.POST);
      rolesChoices.value = rolesSelectable;
      familiesChoices.value = familiesSelectable;
      dealersChoices.value = dealersSelectable;

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => userFetchEnabled.value,
});

onMounted(async () => {
  userFetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <users-table
        :query-key="'users'"
        :data="users"
        :loading="isPending || isFetching"
        :families="familiesChoices"
        :dealers="dealersChoices"
        :roles="rolesChoices"
        :options="userOptions"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
